import { cn } from '@/shared/utils/cn';

import { CheckIcon } from './icons';

/**
 * Where a thing is in its life, as a row of steps: done, current, not yet.
 *
 * The quote editor uses it so that "aprobar" is never a surprise: the person sees that
 * approving is step 4 of 5 and that the PDF and the sending come after, before pressing
 * anything.
 *
 * Compact on purpose: number and name, no description under the current step (`hint`
 * is still accepted and goes to the `title` of the step). On a phone the row does not
 * fit, so it becomes one line: «Paso 2 de 5 · Elegir cliente».
 */
export interface Step {
  key: string;
  label: string;
  hint?: string;
}

export function Stepper({
  steps,
  current,
  cancelled = false,
  label = 'Pasos',
  className,
}: {
  steps: Step[];
  /** Index of the step the thing is on; every step before it is done. */
  current: number;
  /** The path was abandoned: everything reads as struck through. */
  cancelled?: boolean;
  label?: string;
  className?: string;
}) {
  const shown = Math.min(Math.max(current, 0), Math.max(steps.length - 1, 0));
  const currentStep = steps[shown];
  const mobileLine = cancelled
    ? 'Anulado'
    : currentStep
      ? `Paso ${shown + 1} de ${steps.length} · ${currentStep.label}`
      : '';

  return (
    <div className={className}>
      <ol aria-label={label} className="hidden flex-wrap items-center gap-x-1.5 gap-y-2 sm:flex">
        {steps.map((step, index) => {
          const done = !cancelled && index < current;
          const active = !cancelled && index === current;
          return (
            <li key={step.key} className="flex items-center gap-1.5" title={step.hint}>
              <span
                aria-hidden="true"
                className={cn(
                  'inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ring-1 ring-inset',
                  done && 'bg-ok text-white ring-ok',
                  active && 'bg-accent text-white ring-accent',
                  !done && !active && 'bg-card text-ink-4 ring-line',
                )}
              >
                {done ? <CheckIcon size={12} strokeWidth={2.5} /> : index + 1}
              </span>
              <span
                className={cn(
                  'text-[13px] leading-5 font-medium whitespace-nowrap',
                  active ? 'text-ink' : done ? 'text-ink-2' : 'text-ink-4',
                  cancelled && 'text-ink-4 line-through',
                )}
              >
                {step.label}
                {active ? <span className="sr-only"> (paso actual)</span> : null}
              </span>
              {index < steps.length - 1 ? (
                <span aria-hidden="true" className="text-line mx-0.5 select-none">
                  /
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      {mobileLine ? (
        <p
          aria-label={label}
          className={cn(
            'text-[13px] leading-5 font-medium sm:hidden',
            cancelled ? 'text-ink-4 line-through' : 'text-ink-2',
          )}
        >
          {mobileLine}
        </p>
      ) : null}
    </div>
  );
}
